import { useRef, useImperativeHandle, useEffect, Ref, KeyboardEvent, ChangeEvent, ClipboardEvent } from 'react';

import { SECURITY_CODE_LENGTH } from '../securityCodeConstants';

export type SecurityCodeInputHandle = {
  focusFirst: () => void;
};

type UsePasscodeInputProps = {
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
  onSubmit?: () => void;
};

export const useSecurityCodeInput = (
  { value, onChange, autoFocus, onSubmit }: UsePasscodeInputProps,
  ref?: Ref<SecurityCodeInputHandle | null>
) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const skipFocusRedirectRef = useRef(false);
  const digits = value.padEnd(SECURITY_CODE_LENGTH, ' ').split('').slice(0, SECURITY_CODE_LENGTH);

  useImperativeHandle(ref, () => ({
    focusFirst: () => inputRefs.current[0]?.focus(),
  }));

  useEffect(() => {
    if (autoFocus) inputRefs.current[0]?.focus();
  }, [autoFocus]);

  const focusInput = (index: number) => {
    inputRefs.current[index]?.focus();
  };

  const commitDigit = (char: string, focusIndex: number) => {
    if (!/^\d$/.test(char)) return;

    const firstEmptyIndex = digits.findIndex((d) => !d.trim());
    // When all boxes are filled, replace the currently focused box (not always the last one).
    // This lets the user edit any position after filling all digits.
    const insertIndex = firstEmptyIndex === -1 ? focusIndex : firstEmptyIndex;
    const newDigits = [...digits];
    newDigits[insertIndex] = char;
    onChange(newDigits.join('').trimEnd());

    const nextEmptyIndex = newDigits.findIndex((d) => !d.trim());
    if (nextEmptyIndex === -1) {
      if (insertIndex < SECURITY_CODE_LENGTH - 1) {
        focusInput(insertIndex + 1);
      } else {
        focusInput(SECURITY_CODE_LENGTH - 1);
      }
    } else {
      focusInput(nextEmptyIndex);
    }
  };

  const handleChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1);
    if (!char) return;
    commitDigit(char, index);
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (value.trim().length !== SECURITY_CODE_LENGTH) return;
      const form = (e.target as HTMLInputElement).form;
      if (form) form.requestSubmit();
      else onSubmit?.();
      return;
    }

    if (/^\d$/.test(e.key)) {
      const el = e.currentTarget;
      if (el.value.length > 0) {
        e.preventDefault();
        commitDigit(e.key, index);
      }
      return;
    }

    if (e.key === 'Backspace') {
      e.preventDefault();
      const newDigits = [...digits];

      if (digits[index].trim()) {
        newDigits[index] = ' ';
        onChange(newDigits.join('').trimEnd());
        if (index > 0) {
          skipFocusRedirectRef.current = true;
          focusInput(index - 1);
        }
      } else if (index > 0) {
        newDigits[index - 1] = ' ';
        onChange(newDigits.join('').trimEnd());
        skipFocusRedirectRef.current = true;
        focusInput(index - 1);
      }
    }

    if (e.key === 'ArrowLeft' && index > 0) focusInput(index - 1);
    if (e.key === 'ArrowRight' && index < SECURITY_CODE_LENGTH - 1) focusInput(index + 1);
  };

  const handleFocus = (index: number) => {
    if (skipFocusRedirectRef.current) {
      skipFocusRedirectRef.current = false;
      return;
    }
    if (!digits[index].trim()) return;

    const firstEmptyIndex = digits.findIndex((d) => !d.trim());
    if (firstEmptyIndex !== -1) focusInput(firstEmptyIndex);
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, SECURITY_CODE_LENGTH);
    if (!pasted) return;

    onChange(pasted);
    const firstEmptyIndex = pasted.length < SECURITY_CODE_LENGTH ? pasted.length : SECURITY_CODE_LENGTH - 1;
    focusInput(firstEmptyIndex);
  };

  return { inputRefs, digits, focusInput, handleChange, handleKeyDown, handleFocus, handlePaste };
};
